import { useCallback } from "react";
import { TriggeredHook } from "sonobello.utilities.react";

import ApiRequestPaths from "../../constants/ApiRequestPaths";
import { useObx } from "../../hooks/useObx";

/**
 * A record of the medical characteristics of a patient which may qualify or disqualify them from receiving business
 * services.
 */
export interface IContactPatientQualificationRequest {
  /** The height of the patient in inches. */
  heightInches?: number;
  /** The weight of the patient in imperial pounds. */
  weight?: number;
  /** The calculated body mass index of the patient from the height and weight parameters. */
  bodyMassIndex?: number;
  /** The date of birth of the patient as an ISO date. */
  dateOfBirth?: string;
  /** Flag indicating if the patient successfully passed all medical qualification questions. */
  isMedicallyQualified?: boolean;
}

export interface IUseContactPatientQualificationProps {
  /** The id of the contact associated with the qualification request. */
  contactId: string;
  /** The id of the patient associated with the qualification request. */
  patientId: string;
}

const useContactPatientQualification: TriggeredHook<
  IContactPatientQualificationRequest,
  IContactPatientQualificationRequest,
  boolean,
  IUseContactPatientQualificationProps
> = ({ contactId, patientId }) => {
  const { res, err, loading, setReq } = useObx<
    IContactPatientQualificationRequest,
    IContactPatientQualificationRequest
  >("Post Patient Qualification", {
    method: "post",
    url: ApiRequestPaths.postPatientQualification(contactId, patientId)
  });

  const execute = useCallback(
    (payload: IContactPatientQualificationRequest) => {
      setReq(r => r && { ...r, payload });
    },
    [setReq]
  );

  return {
    result: res?.payload ?? null,
    error: Boolean(err),
    isLoading: loading,
    execute
  };
};

export default useContactPatientQualification;
